/**
 * 게시판
 */
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import Header from '@features/content/Header';
import PagingMore from '@components/contents/common/PagingMore';
import { apiCommunitySlice } from '@features/api/apiCommunitySlice';
import { setRequestToSearchParams } from '@utils/index';
import { useOption } from '@hooks/useOption';
import { ALIAS, SEARCH_PARAMS } from '@constants/router';
import { getLocalData } from '@features/api/apiLocalSlice';
import { CUR_NETWORK, NETWORK, FILTER_DATA, SERVICE_ALIAS, COMMUNITY_SERVER_TYPE } from '@constants/index';
import { useAppSelector } from '@hooks/defineRtkTypedHook';
import { selectKeyword, selectL10N, setAlias } from '@features/ui/UiSlice';
import CommunityArticle from '@components/contents/article/CommunityArticle';
import NoData from '../../components/contents/common/NoData';
import sessionStorage from '@store/sessionStorage';
import Filter, { setl10nKeyToTitle } from '@components/contents/common/Filter';
import usePrevious from '@hooks/usePrevious';
import type { ICommunity, ILoaderData, l10nFilterType, ICommunityItem } from '@type/index';

let prevParams: { [key: string]: string } = {};
let result: Promise<[PromiseSettledResult<any>, PromiseSettledResult<any>]> | null = null;
let boardAliasDataTempArr: any = [];
let dropdownMap: { [key: string]: any } = {};
let communityStackData: ICommunityItem[] = [];

// TODO. history back했을 때 session / local data 처리 필요

export const CommunityLoader =
  (dispatch, option: IOption) =>
  async ({ request }): Promise<[PromiseSettledResult<any>, PromiseSettledResult<any>] | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.COMMUNITY));

    if (!searchParams?.keyword) {
      return null;
    }

    const { serviceAlias } = option || {};
    let boardAliasDataIndex =
      serviceAlias === SERVICE_ALIAS.LINEAGE2 && searchParams?.server === COMMUNITY_SERVER_TYPE[SERVICE_ALIAS.LINEAGE2].L2AWKN ? 1 : 0;
    let boardApi = apiCommunitySlice?.endpoints?.getCommunityAll;

    // boardAlias는 array에 저장 (L2일 때, 본서버/각성서버 각각 저장 필요)
    if (Array.isArray(boardAliasDataTempArr) && !boardAliasDataTempArr[boardAliasDataIndex]) {
      const params = { [SEARCH_PARAMS.SERVER]: searchParams[SEARCH_PARAMS.SERVER] || '' };
      boardAliasDataTempArr[boardAliasDataIndex] =
        CUR_NETWORK === NETWORK.OPDEV
          ? getLocalData(ALIAS.COMMUNITY_BOARD_ALIAS)
          : dispatch(apiCommunitySlice?.endpoints?.getBoardAlias?.initiate({ option, params }));
    }

    // boardAlias가 있으면 특정 게시판 조회
    if (searchParams?.boardAlias) {
      boardApi = apiCommunitySlice?.endpoints?.getCommunityArticle;
    }

    result = Promise.allSettled([
      boardAliasDataTempArr[boardAliasDataIndex],
      CUR_NETWORK === NETWORK.OPDEV ? getLocalData(ALIAS.COMMUNITY, searchParams) : dispatch(boardApi?.initiate({ option, params: searchParams })),
    ]);

    prevParams = searchParams;

    return result;
  };

const setFilterData = (serviceAlias: string, boardAliasData: any) => {
  const filterData = FILTER_DATA[serviceAlias][ALIAS.COMMUNITY];
  let newBoardAliasData = [...filterData[SEARCH_PARAMS.BOARD_ALIAS]] as l10nFilterType;

  if (Array.isArray(boardAliasData) && boardAliasData?.length > 0) {
    boardAliasData?.forEach((v) => {
      const { boardName, boardAlias } = v?.root?.board || {};
      newBoardAliasData?.push({
        title: boardName,
        value: boardAlias,
      });
    });
  }

  return { ...filterData, [SEARCH_PARAMS.BOARD_ALIAS]: newBoardAliasData };
};

interface IFilterComponentProps {
  boardAliasFetchData: ILoaderData<ICommunity>;
  count: number;
  setTempData: (tempData: ICommunityItem[]) => void;
}
const FilterComponent = ({ boardAliasFetchData, count, setTempData }: IFilterComponentProps) => {
  const l10n = useAppSelector(selectL10N);
  const option = useOption();
  const { serviceAlias } = option;
  const { data, isLoading, isError } = (boardAliasFetchData?.value as ILoaderData<ICommunity>) || {};
  const filterDataObj = useMemo(() => (serviceAlias ? setFilterData(serviceAlias, data) : {}), [data]);
  const filterData = useMemo(() => Object.entries(filterDataObj), [filterDataObj]);
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = useAppSelector(selectKeyword);
  const prevKeyword = usePrevious(keyword);
  const server = useMemo(() => searchParams?.get(SEARCH_PARAMS.SERVER), [searchParams]);
  const prevServer = usePrevious(server);
  const [l2ActiveServerAlias, setL2ActiveServerAlias] = useState(searchParams?.get(SEARCH_PARAMS.SERVER) || '');
  const [isInitial, setIsInitial] = useState<boolean>(true);

  // keyword 변경 시
  useEffect(() => {
    if (prevKeyword !== undefined && keyword && prevKeyword !== keyword) {
      setIsInitial(true);
    }
  }, [keyword]);

  // 초기화 여부 변경 시
  useEffect(() => {
    if (isInitial) {
      communityStackData = [];
      setIsInitial(false);
    }
  }, [isInitial]);

  // 필터 - 게시판 변경 시
  useEffect(() => {
    const boardAliasParams = searchParams?.get(SEARCH_PARAMS.BOARD_ALIAS) || '';

    if (option?.serviceAlias === SERVICE_ALIAS.LINEAGE2 && searchParams?.get(SEARCH_PARAMS.SERVER)) {
      dropdownMap[SEARCH_PARAMS.BOARD_ALIAS]?.reset({ data: setl10nKeyToTitle(filterDataObj[SEARCH_PARAMS.BOARD_ALIAS], l10n!) });
      dropdownMap[SEARCH_PARAMS.BOARD_ALIAS]?.activeValue([boardAliasParams]);
    }
  }, [filterDataObj[SEARCH_PARAMS.BOARD_ALIAS]]);

  // L2 - 서버 변경 시
  useEffect(() => {
    if (serviceAlias !== SERVICE_ALIAS.LINEAGE2) {
      return;
    }

    if (prevServer === undefined) {
      return;
    }

    if (prevServer !== l2ActiveServerAlias) {
      searchParams?.delete(SEARCH_PARAMS.BOARD_ALIAS);
      searchParams?.set(SEARCH_PARAMS.SERVER, l2ActiveServerAlias);
      searchParams?.set(SEARCH_PARAMS.PAGE, '1');
      setSearchParams(searchParams);
    }
  }, [l2ActiveServerAlias]);

  const filterChangeCallback = useCallback((name: string) => {
    setIsInitial(true);
  }, []);

  const l2ServerAliasChangeCallback = useCallback((name: string, activeData: { d1: string }) => {
    setIsInitial(true);
    setL2ActiveServerAlias(activeData?.d1 || '');
  }, []);

  const setDropdownMap = useCallback(
    (data: { [key: string]: any }) => {
      dropdownMap = { ...dropdownMap, ...data };
    },
    [dropdownMap]
  );

  return (
    <Header totalCount={count}>
      {Array.isArray(filterData) && filterData?.length > 0 ? (
        filterData?.map((data) => (
          <Filter
            key={data[0]}
            filterData={setl10nKeyToTitle(data[1], l10n!)}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            useEachChangeCallback={serviceAlias === SERVICE_ALIAS.LINEAGE2 && data[0] === SEARCH_PARAMS.SERVER}
            changeCallback={
              serviceAlias === SERVICE_ALIAS.LINEAGE2 && data[0] === SEARCH_PARAMS.SERVER ? l2ServerAliasChangeCallback : filterChangeCallback
            }
            setDropdownMap={setDropdownMap}
            option={{
              ...option,
              name: data[0],
              useAllBlankValue: data[1]?.filter((v) => !v?.value)?.length > 0,
            }}
          />
        ))
      ) : (
        <Fragment />
      )}
    </Header>
  );
};

interface IContentComponentProps {
  data: ICommunity;
  tempData: ICommunityItem[];
}
const ContentComponent = ({ data, tempData }: IContentComponentProps) => {
  const l10n = useAppSelector(selectL10N);

  return (
    <Fragment>
      <div className="search-content">
        <ul className="column column-1">
          {tempData?.map((item, i) => (
            <li key={i} className="search-item">
              <CommunityArticle item={item} l10n={l10n!} />
            </li>
          ))}
        </ul>
      </div>
      <PagingMore hasMore={data?.hasMore && tempData?.length < data?.searchCount} l10n={l10n!} />
    </Fragment>
  );
};

const Community = () => {
  const [boardAliasFetchData, communityFetchData] = useLoaderData() || [];
  const { data, isLoading, isError } = (communityFetchData?.value as ILoaderData<ICommunity>) || {};
  const [tempData, setTempData] = useState<ICommunityItem[]>([]);

  useEffect(() => {
    if (Array.isArray(data?.contentList) && data?.contentList?.length > 0) {
      setTempData([...communityStackData, ...data.contentList]);
    }
  }, [data?.contentList]);

  useEffect(() => {
    communityStackData = tempData;
  }, [tempData]);

  return (
    <div className="search-result community">
      <FilterComponent boardAliasFetchData={boardAliasFetchData} count={data?.searchCount} setTempData={setTempData} />
      {!isError && Array.isArray(data?.contentList) && data?.contentList?.length > 0 ? (
        <ContentComponent data={data} tempData={tempData} />
      ) : (
        <NoData />
      )}
    </div>
  );
};

export default Community;
